/**
 * CompanyRegistrationHub — 2-card hub reached from
 * `/manage/cloudgm-agent/company-registration`.
 *
 *   1) تسجيل شركة جديدة  → /manage/cloudgm-agent/company-registration/new
 *   2) متابعة تسجيل الشركات → /manage/cloudgm-agent/company-registration/track
 *
 * (iter-220, Feb 2026)
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FilePlus, ClipboardList } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { AgentPageShell } from './agent/AgentPageShell';
import { usePerms } from '../permissions/PermissionsContext';

const cards = [
  {
    id: 'new-reg',
    icon: FilePlus,
    color: '#10b981',
    title_ar: 'تسجيل شركة جديدة',
    title_en: 'Register New Company',
    desc_ar: 'إنشاء طلب تسجيل شركة جديد',
    desc_en: 'Create a new company registration request',
    path: '/manage/cloudgm-agent/company-registration/new',
    testid: 'reg-new-btn',
  },
  {
    id: 'track-reg',
    icon: ClipboardList,
    color: '#3b82f6',
    title_ar: 'متابعة تسجيل الشركات',
    title_en: 'Track Registrations',
    desc_ar: 'عرض ومتابعة كل طلبات تسجيل الشركات',
    desc_en: 'View and track all company registration requests',
    path: '/manage/cloudgm-agent/company-registration/track',
    testid: 'reg-track-btn',
  },
];

export default function CompanyRegistrationHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC (للإشراف)', label_en: 'CLOUDGM FZ LLC (Supervision)', path: '/dashboard?platform=fzlc' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'تسجيل الشركات', label_en: 'Company Registration', path: null },
  ];

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent')}>
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-3 text-white">
          {isAr ? 'تسجيل الشركات' : 'Company Registration'}
        </h1>
        <p className="text-[#808080]">
          {isAr ? 'اختر البداية أو المتابعة' : 'Start a new request or track existing ones'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {visiblePages(cards, 'path').map((c, i) => {
          const Icon = c.icon;
          return (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              whileHover={{ y: -6, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(c.path)}
              data-testid={c.testid}
              className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 hover:bg-white/[0.04] rounded-2xl p-8 text-center overflow-hidden transition-all cursor-pointer"
            >
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ boxShadow: 'inset 0 0 0 1px ' + c.color + '60, 0 8px 32px ' + c.color + '20' }}
              />
              <div className="relative">
                <div
                  className="w-20 h-20 mx-auto mb-4 rounded-xl flex items-center justify-center border transition-transform duration-300"
                  style={{ backgroundColor: c.color + '15', borderColor: c.color + '30' }}
                >
                  <Icon className="h-10 w-10" style={{ color: c.color }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold mb-2 text-white">
                  {isAr ? c.title_ar : c.title_en}
                </h3>
                <p className="text-xs text-[#808080] px-2">
                  {isAr ? c.desc_ar : c.desc_en}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </AgentPageShell>
  );
}
